import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DistrictSidebar from "../../components/DistrictSidebar";
import api from "../../services/api";
import { downloadEsmpFile } from "../../utils/fileDownload";

export default function ReturnedESMP() {
  const [search, setSearch] = useState("");
  const [esmps, setEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEsmp, setSelectedEsmp] = useState(null);
  const [correctedFile, setCorrectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const correctedFileInputRef = useRef(null);

  const fetchMyEsmps = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/district/my-esmps");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setEsmps(data);
    } catch (err) {
      console.error("Failed to load returned ESMPs:", err);
      setEsmps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyEsmps();
  }, [fetchMyEsmps]);

  const filtered = useMemo(
    () =>
      esmps
        .filter((e) => {
          const status = String(e.status || "").trim().toLowerCase();
          return [
            "returned",
            "rejected",
            "returned for revision",
            "return for revision",
          ].includes(status);
        })
        .filter(
          (e) =>
            (e.project_name || "").toLowerCase().includes(search.toLowerCase()) ||
            (e.district || "").toLowerCase().includes(search.toLowerCase()) ||
            (e.User?.name || "").toLowerCase().includes(search.toLowerCase())
        ),
    [esmps, search]
  );

  const closeModal = () => {
    if (uploading) return;
    setSelectedEsmp(null);
    setCorrectedFile(null);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      units.length - 1
    );
    return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files && event.currentTarget.files[0];
    setCorrectedFile(file || null);
  };

  const handleResubmit = async () => {
    const file =
      correctedFile ||
      (correctedFileInputRef.current?.files &&
        correctedFileInputRef.current.files[0]);

    if (!selectedEsmp) {
      window.alert("Please select the ESMP to resubmit.");
      return;
    }

    if (!file) {
      window.alert("Please select the corrected ESMP file.");
      return;
    }

    const confirmed = window.confirm(
      `Replace the rejected document for ${selectedEsmp.esmp_id} with "${file.name}"? The ESMP ID and project details will remain unchanged.`
    );
    if (!confirmed) return;

    const formData = new FormData();
    formData.append("esmp_file", file);

    try {
      setUploading(true);
      setUploadProgress(0);
      const response = await api.put(
        `/district/resubmit/${selectedEsmp.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (event.total) {
              setUploadProgress(Math.round((event.loaded * 100) / event.total));
            }
          },
        }
      );

      window.alert(
        response.data?.message || "Corrected ESMP resubmitted successfully."
      );
      setSelectedEsmp(null);
      setCorrectedFile(null);
      setUploadProgress(0);
      await fetchMyEsmps();
    } catch (err) {
      console.error("Failed to resubmit corrected ESMP:", err);
      window.alert(
        err.response?.data?.message ||
          "The corrected ESMP could not be resubmitted. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      <DistrictSidebar />
      <main className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Returned / Rejected ESMPs</h2>
          <input
            type="text"
            placeholder="Search ESMP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 rounded text-black border focus:ring-2 focus:ring-green-600 outline-none"
          />
        </div>

        <div className="bg-white p-6 rounded-xl shadow overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">ESMP ID</th>
                <th className="border p-2 text-left">District</th>
                <th className="border p-2 text-left">Project Name</th>
                <th className="border p-2 text-left">Submitted By</th>
                <th className="border p-2 text-left">Reviewed By</th>
                <th className="border p-2 text-left">Status</th>
                <th className="border p-2 text-left">Reviewer Comments</th>
                <th className="border p-2 text-left">Document</th>
                <th className="border p-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="border p-2 text-center" colSpan={9}>
                    Loading ESMPs...
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((item) => (
                  <tr key={item.id}>
                    <td className="border p-2">{item.esmp_id}</td>
                    <td className="border p-2">{item.district}</td>
                    <td className="border p-2">{item.project_name}</td>
                    <td className="border p-2">{item.User?.name || "N/A"}</td>
                    <td className="border p-2">{item.reviewer_name || "N/A"}</td>
                    <td className="border p-2">{item.status}</td>
                    <td className="border p-2">{item.review_comments || "N/A"}</td>
                    <td className="border p-2">
                      {item.file_path ? (
                        <button
                          type="button"
                          onClick={() => downloadEsmpFile(item)}
                          className="text-blue-600 hover:underline"
                        >
                          {item.file_name || "Open document"}
                        </button>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="border p-2">
                      <button
                        type="button"
                        onClick={() => setSelectedEsmp(item)}
                        className="whitespace-nowrap rounded bg-green-600 px-3 py-2 text-white hover:bg-green-700"
                      >
                        Re-upload Corrected ESMP
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="border p-2 text-center" colSpan={9}>
                    No returned or rejected ESMPs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {selectedEsmp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  Re-upload Corrected ESMP
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  The new document will replace the rejected version.
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={uploading}
                className="text-2xl leading-none text-gray-500 hover:text-gray-800"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="mb-5 grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-4 text-sm">
              <div>
                <span className="block text-gray-500">ESMP ID</span>
                <strong>{selectedEsmp.esmp_id}</strong>
              </div>
              <div>
                <span className="block text-gray-500">Status</span>
                <strong>{selectedEsmp.status}</strong>
              </div>
              <div className="col-span-2">
                <span className="block text-gray-500">Project</span>
                <strong>{selectedEsmp.project_name}</strong>
              </div>
              <div className="col-span-2">
                <span className="block text-gray-500">Reviewer comments</span>
                <span>{selectedEsmp.review_comments || "No comments provided."}</span>
              </div>
            </div>

            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Select corrected file
            </label>
            <input
              ref={correctedFileInputRef}
              type="file"
              name="esmp_file"
              onChange={handleFileChange}
              disabled={uploading}
              className="w-full rounded-lg border border-gray-300 p-3"
            />

            {correctedFile && (
              <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
                <strong className="block break-all text-green-800">
                  {correctedFile.name}
                </strong>
                <span className="text-green-700">
                  {formatFileSize(correctedFile.size)}
                </span>
              </div>
            )}

            {uploading && (
              <div className="mt-5">
                <div className="mb-1 flex justify-between text-sm text-gray-600">
                  <span>Uploading corrected ESMP...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-green-600 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={uploading}
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResubmit}
                disabled={uploading}
                className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {uploading ? "Uploading..." : "Replace and Resubmit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
